// Types aligned with CLI/API contracts (CheckoutRequestV1 / CheckoutResponseV1)

// --- Sandbox State (matches backend SandboxState enum) ---

export type SandboxState =
  | 'Creating'
  | 'Running'
  | 'Terminated'
  | 'Deleting'
  | 'Deleted'
  | 'Failed';

export type TerminationReason =
  | 'UserRequested'
  | 'MaxLifetimeExceeded'
  | 'MaxIdleExceeded'
  | 'SystemError';

// --- Port Configuration (matches PortConfigurationV1) ---

export type PortAuthPolicy = 'Owner' | 'Custom';
export type PortProtocol = 'Http' | 'Http2';

export interface PortConfiguration {
  port: number;
  policy: PortAuthPolicy;
  protocol: PortProtocol;
  proxyUri?: string;
  authorizedCallers?: { tenantId: string; objectId: string }[];
}

// --- Sandbox Shape (matches SandboxShapeV1) ---

export interface SandboxShape {
  processorCount: string | number; // K8s string ("2", "500m") or millicores number (2000)
  memorySize: string | number;     // K8s string ("4Gi") or bytes number (4294967296)
}

// --- Sandbox Policies (matches SandboxPoliciesV1) ---

export interface SandboxPolicies {
  maxIdleInSeconds: number;      // default 3600
  maxLifetimeInSeconds: number;  // default 86400
}

// --- Owner Identity ---

export interface SandboxOwner {
  tenantId: string;
  objectId: string;
}

// --- Checkout Request (matches CheckoutRequestV1) ---

export interface CheckoutRequest {
  sandboxName: string;
  imageId: string;
  shape: SandboxShape;
  policies: SandboxPolicies;
  minImageRevision?: number;
  ports?: PortConfiguration[];
  tags?: Record<string, string>;
}

// --- Checkout Response / Sandbox (matches CheckoutResponseV1) ---

export interface Sandbox {
  sandboxId: string;
  poolId: string;
  sandboxName: string;
  imageId: string;
  shape: SandboxShape;
  policies?: SandboxPolicies;
  state: SandboxState;
  owner: SandboxOwner;
  createdAt: string; // ISO datetime
  proxyUri?: string;
  toolUri?: string;
  expiresAt?: string; // ISO datetime
  terminationReason?: TerminationReason;
  ports?: PortConfiguration[];
  tags?: Record<string, string>;
}

// --- Pool ---

export interface Pool {
  poolId: string;
  displayName: string;
  region: string;
  imageIds: string[];
}

// --- Image Catalog (available images per pool) ---

export interface ImageCatalogEntry {
  imageId: string;
  name: string;
  description?: string;
  os: 'windows' | 'linux';
  latestRevision: number;
  tags: string[];          // e.g. ["ltsc2022", "latest"]
  createdAt: string;
}

// --- Auth ---

export type AppRole = 'W365LithiumMgmt.Read' | 'W365LithiumMgmt.ReadWrite';

export interface AuthUser {
  name: string;
  email: string;
  initials: string;
  roles: AppRole[];
}

// --- CLI Config (mirrors `config set` keys) ---

export interface PortalConfig {
  endpoint: string;
  pool: string;
  processorCount: string;
  memorySize: string;
  maxIdleInSeconds: number;
  maxLifetimeInSeconds: number;
  tenant: string;
  scope: string;
}

// --- Terminal Session ---

export interface TerminalSession {
  sandboxId: string;
  sessionId: string;
  isConnected: boolean;
}

// --- ExecStream WebSocket API types (unchanged) ---

export type ExecStreamMessageType =
  | 'start'
  | 'stdin'
  | 'stdout'
  | 'stderr'
  | 'resize'
  | 'closeStdin'
  | 'exitCode'
  | 'sessionId'
  | 'error';

export interface ExecStreamStartRequest {
  command: string;
  args?: string[];
  environment?: Record<string, string>;
  tty: boolean;
  stdin: boolean;
  height: number;
  width: number;
}

export interface TerminalResize {
  height: number;
  width: number;
}

export interface ExecStreamMessage {
  type: ExecStreamMessageType;
  data?: string; // base64 encoded for stdin/stdout/stderr
  start?: ExecStreamStartRequest;
  resize?: TerminalResize;
  exitCode?: number;
}

// --- Shape Presets (for Checkout Wizard) ---

export interface ShapePreset {
  label: string;
  shape: SandboxShape;
  disabled?: boolean;
}

// Memory is proportional to CPU at 4 GB/vCPU (Standard_D32ads_v5: 32 vCPUs / 128 GB)
export const SHAPE_PRESETS: ShapePreset[] = [
  { label: 'Micro',  shape: { processorCount: '500m', memorySize: '2Gi' }, disabled: true },
  { label: 'Mini',   shape: { processorCount: '2',    memorySize: '4Gi' } },
  { label: 'Small',  shape: { processorCount: '4',    memorySize: '16Gi' }, disabled: true },
  { label: 'Medium', shape: { processorCount: '8',    memorySize: '32Gi' }, disabled: true },
];
